import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { IDataTableRow, TRowAction } from '../../models';

import { Box } from '@src/atoms/Box';
import { Button } from '@src/atoms/Button';
import { Menu } from '@src/atoms/Menu';
import { useOpen } from '@src/hooks/useOpen';
import { Link } from '@src/atoms/Link';
import { Icon } from '@src/atoms/Icon';
import { Text } from '@src/atoms/Text';

import styles from './DataTableBodyRowAction.module.scss';
import { withTags } from '@src/utils/tags';
import { useStore } from 'effector-react';
import { userEffectorModel } from '@src/stores/user';
import { useDataTableContext } from '../../context';
import { TooltipNew } from '@src/atoms/TooltipNew';

interface IDataTableBodyRowActionProps {
  actions: TRowAction[];
  isDisplay: boolean;
  rowData: IDataTableRow['data'];
}

export const DataTableBodyRowAction: FC<IDataTableBodyRowActionProps> = ({ actions, rowData, isDisplay }) => {
  const { api } = useDataTableContext();
  const user = useStore(userEffectorModel.$userStore);

  const { onToggle, onClose, open } = useOpen(false);
  const [parent, setParent] = useState<HTMLButtonElement | null>(null);

  const availableAction = useMemo(
    () => actions?.filter((a) => user && withTags({ tags: a.roles, user })),
    [actions, user],
  );

  const handleClick = useCallback(
    (a: TRowAction) => (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
      onClose();

      if (typeof a.action === 'function') {
        a.action(rowData, api, e);
      }
    },
    [api, onClose, rowData],
  );

  useEffect(() => {
    !isDisplay && onClose();
  }, [isDisplay, onClose]);

  /** В таком случае нет смысла показывать и кнопку и список */
  if (!availableAction || (Array.isArray(availableAction) && availableAction.length === 0)) return null;

  return (
    <Box justifyContent={'end'} alignItems="center" fullWidth className={styles.root}>
      {isDisplay && (
        <>
          <Button id={'editButton'} view={'textLight'} preIcon={'menu'} ref={setParent} onClick={onToggle} />
          <Menu open={open} anchorEl={parent} onClose={onClose}>
            <Menu.Section>
              {availableAction.map((item, index) => {
                return (
                  <Link
                    id={'dataTableRowLink' + index}
                    href={
                      item.isDemoDisabled
                        ? undefined
                        : typeof item.action === 'string'
                        ? item.action
                        : typeof item.action === 'object'
                        ? item.action.href
                        : undefined
                    }
                    onClick={item.isDemoDisabled ? undefined : handleClick(item)}
                    noStyles
                    key={index}
                    target={typeof item.action === 'object' ? item.action.target : undefined}
                  >
                    <Menu.Item>
                      <TooltipNew
                        content={item.isDemoDisabled ? 'Функция недоступна в демо режиме' : ''}
                        placement="top"
                      >
                        <Box gap={4} alignItems={'center'}>
                          {item.icon && <Icon name={item.icon} size={'sm'} />}

                          {typeof item.title === 'string' ? (
                            <Text
                              id={'menuText'}
                              size={'caption'}
                              color={item.isDemoDisabled ? 'basicGreySemiDark' : undefined}
                            >
                              {item.title}
                            </Text>
                          ) : (
                            item.title
                          )}
                        </Box>
                      </TooltipNew>
                    </Menu.Item>
                  </Link>
                );
              })}
            </Menu.Section>
          </Menu>
        </>
      )}
    </Box>
  );
};
